import { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

const damp = (cur, to, dt, k = 4) => cur + (to - cur) * (1 - Math.exp(-k * dt));

export const Particles = ({ origin, count, color, rise, spread, size, opacity, life = 2.4 }) => {
  const g = useRef();
  const seeds = useMemo(() => Array.from({ length: count }, (_, i) => [Math.random() * spread - spread / 2, Math.random() * spread - spread / 2, i / count]), [count, spread]);
  useFrame(({ clock }) => {
    if (!g.current) return;
    g.current.children.forEach((m, i) => {
      const [sx, sz, ph] = seeds[i];
      const t = ((clock.elapsedTime / life) + ph) % 1;
      m.position.set(sx * (0.5 + t), rise[1] * t, sz * (0.5 + t) + rise[2] * t);
      m.position.x += rise[0] * t;
      m.scale.setScalar(size * (0.4 + t * 1.6));
      m.material.opacity = opacity * Math.sin(Math.PI * t);
    });
  });
  return (
    <group ref={g} position={origin}>
      {seeds.map((_, i) => <mesh key={i}><sphereGeometry args={[1, 10, 10]} /><meshStandardMaterial color={color} transparent depthWrite={false} emissive={color} emissiveIntensity={0.3} /></mesh>)}
    </group>
  );
};

const Person = ({ walk, show, bed }) => {
  const g = useRef();
  const t = useRef(0);
  useFrame((_, dt) => {
    if (!g.current) return;
    t.current = damp(t.current, show ? 1 : 0, dt, 1.6);
    const a = new THREE.Vector3(...walk[0]), b = new THREE.Vector3(...walk[1]);
    g.current.position.lerpVectors(a, b, t.current);
    g.current.visible = t.current > 0.03;
    g.current.scale.setScalar(Math.min(1, t.current * 3));
  });
  return (
    <group>
      <group ref={g}>
        <mesh position={[0, 0.75, 0]} castShadow><capsuleGeometry args={[0.18, 0.75, 6, 12]} /><meshStandardMaterial color="#1D4ED8" /></mesh>
        <mesh position={[0, 1.45, 0]} castShadow><sphereGeometry args={[0.15, 16, 16]} /><meshStandardMaterial color="#C08457" /></mesh>
      </group>
      {bed && <mesh position={bed} rotation={[0, 0, Math.PI / 2]}><capsuleGeometry args={[0.16, 0.8, 6, 12]} /><meshStandardMaterial color="#1D4ED8" /></mesh>}
    </group>
  );
};

const Door = ({ cfg, open }) => {
  const g = useRef();
  useFrame((_, dt) => { if (g.current) g.current.rotation.y = damp(g.current.rotation.y, open ? -1.25 : 0, dt, 3); });
  return (
    <group>
      {open && <mesh position={[cfg.hinge[0] + 0.03, 1.05, cfg.hinge[2] + cfg.w / 2]}><boxGeometry args={[0.02, 2.1, cfg.w]} /><meshBasicMaterial color="#0B1220" /></mesh>}
      <group ref={g} position={cfg.hinge}>
        <mesh position={[0.05, 1.05, cfg.w / 2]} castShadow><boxGeometry args={[0.06, 2.1, cfg.w]} /><meshStandardMaterial color="#7C4A2D" /></mesh>
      </group>
    </group>
  );
};

const Lights = ({ room, fx }) => {
  const lamp = useRef(), siren = useRef(), sun = useRef(), lampMat = useRef();
  useFrame(({ clock }, dt) => {
    if (lamp.current) lamp.current.intensity = damp(lamp.current.intensity, fx.lights ? (fx.night ? 9 : 5) : 0, dt, 5);
    if (lampMat.current) lampMat.current.emissiveIntensity = damp(lampMat.current.emissiveIntensity, fx.lights ? 2.5 : 0.05, dt, 5);
    if (siren.current) siren.current.intensity = fx.siren ? (Math.sin(clock.elapsedTime * 14) > 0 ? 14 : 0) : 0;
    if (sun.current) sun.current.intensity = damp(sun.current.intensity, fx.hot ? 6 : 0, dt, 3);
  });
  return (
    <group>
      <pointLight ref={lamp} position={room.lamp} color="#FFD8A0" intensity={0} distance={9} decay={1.6} castShadow />
      <mesh position={[room.lamp[0], 2.96, room.lamp[2]]}><cylinderGeometry args={[0.2, 0.2, 0.02, 24]} /><meshStandardMaterial ref={lampMat} color="#FEF3C7" emissive="#FDBA74" emissiveIntensity={0.05} /></mesh>
      <pointLight ref={siren} position={room.siren} color="#EF4444" intensity={0} distance={10} />
      <pointLight ref={sun} position={[room.sun[0], room.sun[1] + 0.6, room.sun[2] + 0.6]} color="#FB923C" intensity={0} distance={7} />
    </group>
  );
};

export const LabFx = ({ room, fx }) => (
  <group>
    <Lights room={room} fx={fx} />
    <Door cfg={room.door} open={fx.door} />
    <Person walk={room.walk} show={fx.person} bed={fx.bed ? room.bed : null} />
    {fx.ac && <Particles origin={room.ac} count={16} color="#67E8F9" rise={[0, -1.4, 1.6]} spread={0.9} size={0.035} opacity={0.75} life={1.6} />}
    {fx.smoke && <Particles origin={room.fire} count={18} color="#64748B" rise={[0, 2.3, 0]} spread={0.5} size={0.12} opacity={0.55} life={2.6} />}
    {fx.smoke && <pointLight position={room.fire} color="#F97316" intensity={4} distance={3} />}
    {fx.gas && room.gas && <Particles origin={room.gas} count={16} color="#84CC16" rise={[-1.2, 0.25, 0.6]} spread={0.7} size={0.1} opacity={0.4} life={3} />}
    {room.valve && fx.gas && (
      <mesh position={room.valve} rotation={[0, 0, Math.PI / 2]}><torusGeometry args={[0.16, 0.025, 10, 30]} /><meshStandardMaterial color={fx.valve ? "#22C55E" : "#EF4444"} emissive={fx.valve ? "#22C55E" : "#EF4444"} emissiveIntensity={1.4} /></mesh>
    )}
    {room.card && <mesh position={room.card}><boxGeometry args={[0.05, 0.12, 0.085]} /><meshStandardMaterial color="#F59E0B" emissive="#F59E0B" emissiveIntensity={fx.card ? 2 : 0} transparent opacity={fx.card ? 1 : 0} /></mesh>}
  </group>
);
